import useMemoObject from '@dyl/hooks/useMemoObject';
import { without } from 'lodash';
import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

const SafeAreaContext = createContext({
    insets: { top: 0, left: 0, right: 0, bottom: 0 },
    /**
     * @param {'top' | 'left' | 'right' | 'bottom'} side
     * @param {'min' | 'add'} operation
     * @param {number} amount
     * @returns {() => void} unregister
     */
    registerInsetModifier(side, operation, amount) { throw new Error('No SafeAreaManager in tree'); },
});

export default function SafeAreaManager({ children }) {
    const [modifiers, setModifiers] = useState(
        /** @returns {{ side: 'top' | 'left' | 'right' | 'bottom', operation: 'min' | 'add', amount: number }[]} */
        () => []
    );

    const registerInsetModifier = useCallback((side, operation, amount) => {
        const obj = { side, operation, amount };

        setModifiers(prev => [...prev, obj]);

        return () => setModifiers(prev => without(prev, obj));
    }, []);

    const insets = useMemo(() => {
        const insets = { top: 0, left: 0, right: 0, bottom: 0 };

        for (const { side, operation, amount } of modifiers) {
            switch (operation) {
                case 'add':
                    insets[side] += amount;
                    break;
                case 'min':
                    insets[side] = Math.max(insets[side], amount);
                    break;
                default:
                    console.warn(`Unsupported SafeArea operation: ${side} ${operation} ${amount}`);
                    break;
            }
        }

        return insets;
    }, [modifiers]);

    const context = useMemoObject({
        insets,
        registerInsetModifier,
    });

    return (
        <SafeAreaContext.Provider value={context}>
            {children}
        </SafeAreaContext.Provider>
    );
}

export function useSafeArea() {
    const { insets } = useContext(SafeAreaContext);

    return { insets };
}

/**
 * @param {'top' | 'left' | 'right' | 'bottom'} side
 * @param {'min' | 'add'} operation
 * @param {number} amount
 */
export function useSafeAreaModifier(side, operation, amount) {
    const { registerInsetModifier } = useContext(SafeAreaContext);

    // Default to zero for non-numbers, infinities, and NaN.
    if (!Number.isFinite(amount)) {
        amount = 0;
    }

    useLayoutEffect(() => {
        const unregister = registerInsetModifier(side, operation, amount);

        return () => unregister();
    }, [side, operation, amount]);
}